// ─────────────────────────────────────────────────────────────────────────────
// lib/theme.js
// Design tokens centralizzati — equivalente delle CSS variables del progetto web.
// Usare sempre questi valori nei StyleSheet invece di colori hardcoded.
// ─────────────────────────────────────────────────────────────────────────────

// ── Utility ───────────────────────────────────────────────────────────
export const hexToRgba = (hex, opacity) => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${opacity})`
}

// ── Bianco ────────────────────────────────────────────────────────────
const white = '#ffffff'

// ── Default brand (Giro) — fallback se Supabase non risponde ─────────
export const DEFAULT_THEME = {
  brand: '#E8397D',
  brandLight: '#f06292',
  brandDark: '#c4275e',
  brandText: '#ffffff',
}

// ── makeColors — genera i colori a partire dal brand ─────────────────
// Chiamata da ThemeContext con i valori letti da Supabase
export const makeColors = (
  brand = DEFAULT_THEME.brand,
  brandLight = DEFAULT_THEME.brandLight,
  brandDark = DEFAULT_THEME.brandDark,
  brandText = DEFAULT_THEME.brandText,
) => ({
  rosa: brand,
  rosaLight: brandLight,
  rosaDark: brandDark,
  brandText,
  rosaSubtle: hexToRgba(brand, 0.08),
  rosaBorder: hexToRgba(brand, 0.3),
  rosaGlow: hexToRgba(brand, 0.25),

  // Bianco e varianti trasparenti
  white,
  white85: hexToRgba(white, 0.85),
  white80: hexToRgba(white, 0.8),
  white70: hexToRgba(white, 0.7),
  white60: hexToRgba(white, 0.6),
  white20: hexToRgba(white, 0.2),
  white15: hexToRgba(white, 0.15),
  white07: hexToRgba(white, 0.07),

  // Sfondi dark (default)
  bg: '#0D0D0D',
  bgCard: '#161616',
  bgCard2: '#1a1a1a',
  bgCard3: '#1e1e1e',
  bgDeep: '#050505',
  bgDeeper: '#151515',

  // Sfondi light mode
  bgLight: '#e5e5e5',
  bgLight2: '#f8f8f8',

  // Bordi
  border: '#2a2a2a',
  borderLight: '#333333',
  borderMid: '#444444',
  borderLight2: '#eeeeee',
  dividerLight: '#e0e0e0',

  // Testi dark
  text: '#ffffff',
  textMuted: '#888888',
  textDim: '#555555',
  textDark: '#111111',

  // Testi light mode
  textLight: '#bbbbbb',
  textLight2: '#aaaaaa',
  textLight3: '#999999',
  textLight4: '#cccccc',

  // UI controls
  switchTrack: '#444444',
  switchThumb: '#cccccc',

  // Feedback
  success: '#10B981',
  successBg: hexToRgba('#10B981', 0.1),
  successBorder: hexToRgba('#10B981', 0.3),
  warning: '#f59e0b',
  warningBg: hexToRgba('#f59e0b', 0.08),
  warningBorder: hexToRgba('#f59e0b', 0.3),
  info: '#7cb9ff',
  infoBg: hexToRgba('#3b82f6', 0.1),
  infoBorder: hexToRgba('#3b82f6', 0.3),
  error: brand,
  errorBg: hexToRgba(brand, 0.1),

  // Nav
  navBg: 'rgba(13,13,13,0.95)',
  navBorder: '#1f1f1f',

  // Flip
  flip: '#6366f1',
  flipBg: hexToRgba('#6366f1', 0.08),
  flipBorder: hexToRgba('#6366f1', 0.25),

  // Trasparente
  transparent: 'transparent',
})

// ── colors — istanza statica con i default (Giro)
// Usata come fallback e per i file che non hanno ancora il ThemeContext
export const colors = makeColors()

// ── Tipografia ────────────────────────────────────────────────────────
export const fonts = {
  condensed: 'BarlowCondensed_700Bold',
  condensedBold: 'BarlowCondensed_800ExtraBold',
  condensedMedium: 'BarlowCondensed_500Medium',
  condensedRegular: 'BarlowCondensed_400Regular',
  bebasNeue: 'BebasNeue_400Regular',
  orbitron: 'Orbitron_700Bold',
  system: undefined,
  dseg7b: 'DSEG7Classic_Bold',

}

export const fontSizes = {
  xs: 10,
  sm: 11,
  base: 13,
  md: 14,
  lg: 16,
  xl: 18,
  '2xl': 22,
  '3xl': 28,
  '4xl': 36,
}

// ── Spacing ───────────────────────────────────────────────────────────
export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  '2xl': 24,
  '3xl': 32,
}

// ── Border radius ─────────────────────────────────────────────────────
export const radius = {
  sm: 6,
  md: 8,
  lg: 10,
  xl: 14,
  '2xl': 20,
  full: 9999,
}

// ── Shadows ───────────────────────────────────────────────────────────
export const makeShadows = (brand = DEFAULT_THEME.brand) => ({
  rosa: {
    shadowColor: brand,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  card: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 3,
  },
})

export const shadows = makeShadows()

// ── Base components ───────────────────────────────────────────────────
export const makeBase = (c = colors) => ({
  card: {
    backgroundColor: c.bgCard2,
    borderRadius: radius.xl,
    borderWidth: 1,
    borderColor: c.border,
  },
  cardAccent: {
    backgroundColor: c.rosaSubtle,
    borderRadius: radius.xl,
    borderWidth: 1,
    borderColor: c.rosaBorder,
  },
  input: {
    backgroundColor: c.bgCard2,
    borderWidth: 1,
    borderColor: c.border,
    borderRadius: radius.lg,
    padding: spacing.md,
    fontSize: fontSizes.md,
    color: c.text,
  },
  btnPrimary: {
    backgroundColor: c.rosa,
    borderRadius: radius.lg,
    padding: spacing.md,
    alignItems: 'center',
  },
  btnPrimaryText: {
    color: c.brandText,
    fontWeight: '800',
    fontSize: fontSizes.lg,
    letterSpacing: 0.5,
  },
  btnSecondary: {
    backgroundColor: c.bgCard2,
    borderWidth: 1,
    borderColor: c.border,
    borderRadius: radius.lg,
    padding: spacing.md,
    alignItems: 'center',
  },
  sectionLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
    gap: spacing.sm,
  },
  sectionBar: {
    width: 3,
    height: 14,
    backgroundColor: c.rosa,
    borderRadius: 2,
  },
  sectionText: {
    fontSize: fontSizes.xs,
    fontWeight: '800',
    color: c.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
  },
})

export const base = makeBase()

export default { colors, fonts, fontSizes, spacing, radius, shadows, base }